import { useContext } from 'react';
import { Module } from '../api/module';
import IdentityContext from '../contexts/IdentityContext';

export const LICENSE_VALIDATION_TYPES = {
  LICENSED: 'LICENSED',
  STUDENT_NO_DOCUMENT: 'STUDENT_NO_DOCUMENT',
  TEACHER_NO_DOCUMENT: 'TEACHER_NO_DOCUMENT',
  TEACHER_NO_DIGITAL_MANUAL: 'TEACHER_NO_DIGITAL_MANUAL',
} as const;

function useLicenses() {
  const {
    identity: { isTeacherAlike },
  } = useContext(IdentityContext);

  function hasOnlyDemoProductLicenseForTeacher(module: Module | undefined) {
    return (
      module?.licenses.hasDemoProductLicenseForTeacher &&
      !(module.licenses.hasDocumentLicenseForTeacher || module.licenses.hasDocumentLicenseForStudent)
    );
  }

  function getUserLicenseValidationType(module: Module): keyof typeof LICENSE_VALIDATION_TYPES {
    const {
      hasDocumentLicenseForStudent,
      hasDocumentLicenseForTeacher,
      hasDigitalManualLicenseForTeacher,
      hasDemoProductLicenseForTeacher,
    } = module.licenses;

    if (isTeacherAlike) {
      if (hasDemoProductLicenseForTeacher) {
        return LICENSE_VALIDATION_TYPES.LICENSED;
      }

      if (!hasDocumentLicenseForTeacher) {
        return LICENSE_VALIDATION_TYPES.TEACHER_NO_DOCUMENT;
      }
      if (hasDocumentLicenseForTeacher && !hasDigitalManualLicenseForTeacher) {
        return LICENSE_VALIDATION_TYPES.TEACHER_NO_DIGITAL_MANUAL;
      }
    } else {
      if (!hasDocumentLicenseForStudent) {
        return LICENSE_VALIDATION_TYPES.STUDENT_NO_DOCUMENT;
      }
    }

    return LICENSE_VALIDATION_TYPES.LICENSED;
  }

  function isModuleLicensed(module?: Module | null): boolean {
    if (!module) return false;
    return getUserLicenseValidationType(module) === LICENSE_VALIDATION_TYPES.LICENSED;
  }

  return {
    hasOnlyDemoProductLicenseForTeacher,
    getUserLicenseValidationType,
    isModuleLicensed,
  };
}

export default useLicenses;
